import { type Brand, BRAND } from '@/lib/cms/config';

export interface OrganizationContactPoint {
  telephone: string;
  contactType: string;
  areaServed: string;
  availableLanguage: string[];
}

export interface OrganizationData {
  name: string;
  legalName: string;
  foundingDate: string;
  logo: string;
  sameAs: string[];
  contactPoint: OrganizationContactPoint;
}

export interface SeoConstants {
  brand: Brand;
  brandName: string;
  titleSuffix: string;
  fallbackTitle: string;
  fallbackDescription: string;
  baseUrl: string;
  logoUrl: string;
  organization: OrganizationData;
}

const SEO_CONSTANTS: Record<Brand, SeoConstants> = {
  gallet: {
    brand: 'gallet',
    brandName: 'Gallet',
    titleSuffix: '| Gallet',
    fallbackTitle: 'Gallet | Swiss Luxury Watches Since 1826',
    fallbackDescription:
      'Discover Gallet luxury timepieces. As the oldest watch brand in the world, Gallet has been crafting exceptional Swiss watches since 1826.',
    baseUrl: 'https://www.gallet.com',
    logoUrl: '/images/gallet/logo.svg',
    organization: {
      name: 'Gallet',
      legalName: 'Gallet & Cie SA',
      foundingDate: '1826',
      logo: 'https://www.gallet.com/images/gallet/logo.svg',
      sameAs: [
        'https://www.facebook.com/Breitling',
        'https://x.com/Breitling',
        'https://www.instagram.com/breitling',
        'https://www.linkedin.com/company/breitling',
      ],
      contactPoint: {
        telephone: '+1-866-657-7670',
        contactType: 'Customer Service',
        areaServed: 'US',
        availableLanguage: ['English'],
      },
    },
  },
  breitling: {
    brand: 'breitling',
    brandName: 'Breitling',
    titleSuffix: '| Breitling',
    fallbackTitle: 'Breitling® | Swiss Luxury Watches of Style, Purpose & Action',
    fallbackDescription:
      'Breitling is obsessed with quality resulting in beautifully crafted & precise watches. Discover our brand new watch collection!',
    baseUrl: 'https://www.breitling.com',
    logoUrl: '/images/breitling/logo.svg',
    organization: {
      name: 'Breitling',
      legalName: 'Breitling SA',
      foundingDate: '1884',
      logo: 'https://www.breitling.com/images/breitling/logo.svg',
      sameAs: [
        'https://www.facebook.com/Breitling',
        'https://x.com/Breitling',
        'https://www.instagram.com/breitling',
        'https://www.linkedin.com/company/breitling',
      ],
      contactPoint: {
        telephone: '+41-32-654-5454',
        contactType: 'Customer Service',
        areaServed: 'US',
        availableLanguage: ['English'],
      },
    },
  },
  ug: {
    brand: 'ug',
    brandName: 'Universal Genève',
    titleSuffix: '| Universal Genève',
    fallbackTitle: 'Universal Genève | Swiss Watchmaking Since 1894',
    fallbackDescription:
      'Discover Universal Genève, a storied name in Swiss watchmaking since 1894, celebrated for the Polerouter and Compax chronographs.',
    baseUrl: 'https://www.universalgeneve.com',
    logoUrl: '/logos/ug/horizontal_regular.svg',
    organization: {
      name: 'Universal Genève',
      legalName: 'Universal Genève SA',
      foundingDate: '1894',
      logo: 'https://www.universalgeneve.com/logos/ug/horizontal_regular.svg',
      sameAs: [
        'https://www.facebook.com/universalgeneve',
        'https://www.instagram.com/universalgeneve',
      ],
      contactPoint: {
        telephone: '+41-22-319-0000',
        contactType: 'Customer Service',
        areaServed: 'CH',
        availableLanguage: ['English', 'French', 'Italian', 'German'],
      },
    },
  },
};

/**
 * Get SEO constants for the specified brand.
 * Falls back to the current build-time BRAND.
 */
export function getSeoConstants(brand: Brand = BRAND): SeoConstants {
  return SEO_CONSTANTS[brand];
}

/**
 * Get the base URL for canonical/hreflang generation.
 * Uses NEXT_PUBLIC_URL env if set, otherwise the brand default.
 */
export function getBaseUrl(brand: Brand = BRAND): string {
  const raw = process.env.NEXT_PUBLIC_URL || SEO_CONSTANTS[brand].baseUrl;
  return raw.replace(/\/+$/, '');
}

/**
 * Default locale used as x-default hreflang target.
 */
export const X_DEFAULT_LOCALE = 'us-en';
